import { AddCircleOutline } from '@mui/icons-material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import ClientAvatar from 'components/shared/ClientAvatar';
import { SortPopover } from 'components/shared/SortPopOver';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';

import { Theme } from '@emotion/react';
import { CustomPagination } from 'components/featured/Deals/components/DealsTable/Pagination';
import { relationshipColorMap } from '../data';
import { ContactType } from '../types';
import { useSaveToMyContacts } from '../useSaveToMyContacts';
import { Columns, SORT_LABELS } from './data';
import { muiStyles } from './muiStyles';
import { ContactColumnId } from './types';

const TableView = ({
  contacts,
  totalCount,
  totalPages,
  currentPage,
  handlePageChange,
  handleRowsChange,
  limit,
  fetchContacts,
}: {
  contacts: ContactType[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  handlePageChange: (newPage: number) => void;
  handleRowsChange: (newPage: number) => void;
  limit: number;
  fetchContacts: () => Promise<void>;
}) => {
  const { id: businessId } = useParams();
  const { postContact, deleteContact } = useSaveToMyContacts();
  const [sortAnchor, setSortAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);
  const [sortConfig, setSortConfig] = useState<{ field: string; order: 'asc' | 'desc' }>();

  if (!contacts.length) return <>No Contacts</>;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none', marginTop: '24px' }}>
        <TableContainer
          sx={{
            overflow: 'scroll',
            height: 'calc(100vh - 450px)',
          }}
        >
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow sx={muiStyles.headerRowStyles}>
                {Columns.map((column) => {
                  // const isSortable = SORTABLE_COLUMNS.includes(column.id);
                  // NOTE: Hardcoded sorting until backend will integrate sorting for this table
                  const isSortable = false;

                  return (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      onClick={(e) =>
                        isSortable && setSortAnchor({ el: e.currentTarget, id: column.id })
                      }
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant='body3'>{column.label}</Typography>
                        {isSortable && (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                            aria-label='button'
                          >
                            <UnfoldMoreIcon
                              sx={{
                                fontSize: 16,
                                color: zeplinColor.BasicBlack,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={Columns.length} align='center'>
                    <Typography variant='body2'>No contacts found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((row, ind) => {
                  return (
                    <TableRow
                      hover
                      key={ind}
                      sx={{
                        '&:last-child td, &:last-child th': {
                          borderBottom: `2px solid ${zeplinColor.Neutral5}`,
                        },
                        textDecoration: 'none',
                        color: 'inherit',
                        bgcolor: 'none',
                        borderLeft: `2px solid ${zeplinColor.Neutral5}`,
                        cursor: 'pointer',
                      }}
                    >
                      {Columns.map((column) => {
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{
                              padding: '22px 12px',
                              borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                              borderTop: `1px solid ${zeplinColor.Neutral5}`,
                            }}
                          >
                            {renderCell(column.id, row, {
                              businessId: businessId ?? '',
                              postContact,
                              deleteContact,
                              fetchContacts,
                            })}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <SortPopover
        anchorEl={sortAnchor?.el || null}
        setAnchorEl={() => setSortAnchor(null)}
        sortOption={sortConfig && sortConfig.field === sortAnchor?.id ? sortConfig.order : 'asc'}
        onSortChange={(order) => {
          if (sortAnchor) {
            setSortConfig({ field: sortAnchor.id, order });
            // dispatch(setSortOrder(order));
            // dispatch(setSortBy(sortAnchor.id));
            handlePageChange(0);
          }
          setSortAnchor(null);
        }}
        label={Columns.find((c) => c.id === sortAnchor?.id)?.label || ''}
        ascLabel={sortAnchor ? SORT_LABELS[sortAnchor.id]?.asc : ''}
        descLabel={sortAnchor ? SORT_LABELS[sortAnchor.id]?.desc : ''}
      />

      <CustomPagination
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={limit}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsChange}
      />
    </Box>
  );
};

const ContactTableRowActions = ({
  row,
  businessId,
  postContact,
  deleteContact,
  fetchContacts,
}: {
  row: ContactType;
  businessId: string;
  postContact: (id: string, zoominfoPersonId: string) => void;
  deleteContact: (id: string, zoominfoPersonId: string) => void;
  fetchContacts: () => Promise<void>;
}) => {
  const { zoominfoPersonId, isSaved: savedStatus } = row;
  const [isSaved, setIsSaved] = useState(savedStatus);

  useEffect(() => {
    setIsSaved(savedStatus);
  }, [savedStatus]);

  const tooltipMessage = !isSaved ? 'Add to My Contacts' : 'Remove from My Contacts';
  const Icon = !isSaved ? AddCircleOutline : CancelOutlinedIcon;

  const handleContactStatus = async () => {
    try {
      if (!isSaved) {
        await postContact(businessId, zoominfoPersonId);
        setIsSaved((prev) => !prev);
      } else {
        await deleteContact(businessId, zoominfoPersonId);
        setIsSaved((prev) => !prev);
      }
      await fetchContacts();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Tooltip title={tooltipMessage}>
      <IconButton
        size='small'
        sx={{ color: zeplinColor.BasicBlack }}
        onClick={(e) => {
          e.stopPropagation();
          void handleContactStatus();
        }}
      >
        <Icon />
      </IconButton>
    </Tooltip>
  );
};

const renderCell = (
  columnId: ContactColumnId,
  row: ContactType,
  actionProps: {
    businessId: string;
    postContact: (id: string, zoominfoPersonId: string) => void;
    deleteContact: (id: string, zoominfoPersonId: string) => void;
    fetchContacts: () => Promise<void>;
  },
) => {
  if (columnId === 'actions') {
    return <ContactTableRowActions row={row} {...actionProps} />;
  }

  const {
    name,
    profilePicture,
    roles,
    phone,
    email,
    contactRelationship,
    lastContacted,
    department,
  } = row;

  const renders: Record<string, () => JSX.Element | string> = {
    name: () => (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          maxWidth: '240px',
          overflow: 'hidden',
        }}
      >
        <ClientAvatar
          profilePicture={profilePicture}
          clientName={name}
          sx={{ width: '28px', height: '28px' }}
        />
        <Typography noWrap sx={{ textOverflow: 'ellipsis' }}>
          {name ?? 'N/A'}
        </Typography>
      </Box>
    ),
    title: () => (
      <Chip
        label={<Typography variant='label1'>{roles ?? 'N/A'}</Typography>}
        sx={{
          bgcolor: zeplinColor.BasicBlack,
          color: 'white',
          height: '22px',
          fontSize: '12px',
        }}
      />
    ),
    department: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{department ? department : 'N/A'}</Typography>
      </Box>
    ),
    phone: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{phone ? phone : 'N/A'}</Typography>
      </Box>
    ),
    email: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{email ? email : 'N/A'}</Typography>
      </Box>
    ),
    contactStatus: () => (
      <Chip
        label={
          <Typography variant='responsiveLabel1' sx={muiStyles.chipLabel}>
            {Utilities.formatSnakeToTitle(contactRelationship)}
          </Typography>
        }
        sx={[relationshipColorMap[contactRelationship], muiStyles.chip] as SxProps<Theme>}
      />
    ),
    lastContacted: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='responsiveBody3'>{lastContacted ?? 'N/A'}</Typography>
      </Box>
    ),
  };

  return renders[columnId] ? renders[columnId]() : row[columnId as keyof ContactType];
};

export default TableView;
